import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DollarSign, Calendar, CheckCircle, AlertTriangle, XCircle, Plus, Eye, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Payments = () => {
  const { user, token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [files, setFiles] = useState([]);
  const [tasks, setTasks] = useState([]); // ✅ AJOUT: State pour les tâches
  const [selectedTasks, setSelectedTasks] = useState([]); // ✅ AJOUT: Tâches sélectionnées
  const [newPayment, setNewPayment] = useState({
    file_id: '',
    client_id: '',
    assigned_employee_id: '',
    amount: '',
    description: '',
    due_date: '',
    payment_method: '',
    notes: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchStats();
    if (user?.role === 'admin' || user?.role === 'sub_admin') {
      fetchClients();
      fetchEmployees();
      fetchFiles();
    }
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, filterStatus]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setPayments(response.data);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement des paiements');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/payments/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setClients(response.data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setEmployees(response.data.filter(u => u.role === 'employee' || u.role === 'admin' || u.role === 'sub_admin'));
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  // ✅ AJOUT: Fonction pour récupérer les tâches d'un dossier
  const fetchTasksForFile = async (fileId) => {
    if (!fileId) {
      setTasks([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { file_id: fileId }
      });
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    }
  };

  const filterPayments = () => {
    if (filterStatus === 'all') {
      setFilteredPayments(payments);
    } else {
      setFilteredPayments(payments.filter(p => p.status === filterStatus));
    }
  };

  const handleCreatePayment = async () => {
    if (!newPayment.file_id || !newPayment.client_id || !newPayment.assigned_employee_id || !newPayment.amount || !newPayment.description || !newPayment.due_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      // ✅ AJOUT: Inclure les tâches complétées dans le paiement
      const paymentData = {
        ...newPayment,
        completed_tasks: selectedTasks // IDs des tâches cochées
      };
      
      await axios.post(`${API}/payments`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paiement créé avec succès');
      setCreateDialogOpen(false);
      setNewPayment({
        file_id: '',
        client_id: '',
        assigned_employee_id: '',
        amount: '',
        description: '',
        due_date: '',
        payment_method: '',
        notes: ''
      });
      setTasks([]); // ✅ AJOUT: Réinitialiser les tâches
      setSelectedTasks([]); // ✅ AJOUT: Réinitialiser la sélection
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la création du paiement');
    }
  };

  const handleMarkPaid = async (paymentId) => {
    const paymentMethod = window.prompt('Méthode de paiement (virement, carte, especes, cheque):');
    if (!paymentMethod) return;

    const reference = window.prompt('Numéro de référence (optionnel):');

    try {
      await axios.post(`${API}/payments/${paymentId}/mark-paid`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { payment_method: paymentMethod, reference: reference || undefined }
      });
      toast.success('Paiement marqué comme payé et reçu généré automatiquement! ✅');
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return;

    try {
      await axios.delete(`${API}/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paiement supprimé');
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      en_attente: { color: 'bg-yellow-100 text-yellow-800', icon: <Calendar className="h-4 w-4" />, text: 'En attente' },
      paye: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="h-4 w-4" />, text: 'Payé' },
      en_retard: { color: 'bg-red-100 text-red-800', icon: <AlertTriangle className="h-4 w-4" />, text: 'En retard' },
      annule: { color: 'bg-gray-100 text-gray-800', icon: <XCircle className="h-4 w-4" />, text: 'Annulé' }
    };

    const badge = badges[status] || badges.en_attente;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        {badge.icon}
        {badge.text}
      </Badge>
    );
  };

  const isOverdue = (payment) => {
    if (payment.status !== 'en_attente' && payment.status !== 'en_retard') return false;
    return new Date(payment.due_date) < new Date();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              💳 Suivi des Paiements
            </h1>
            <p className="text-gray-600 mt-2">
              {user?.role === 'employee' 
                ? 'Gérez les paiements de vos clients assignés'
                : user?.role === 'admin' || user?.role === 'sub_admin'
                ? 'Vue complète de tous les paiements'
                : 'Vos paiements en cours'}
            </p>
          </div>
          {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'employee') && (
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau paiement
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total</p>
                    <p className="text-3xl font-bold text-blue-900">{stats.total}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">En attente</p>
                    <p className="text-3xl font-bold text-yellow-900">{stats.pending}</p>
                    <p className="text-xs text-gray-600 mt-1">{stats.total_amount_pending.toFixed(2)}$</p>
                  </div>
                  <Calendar className="h-10 w-10 text-yellow-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">En retard</p>
                    <p className="text-3xl font-bold text-red-900">{stats.overdue}</p>
                  </div>
                  <AlertTriangle className="h-10 w-10 text-red-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payés</p>
                    <p className="text-3xl font-bold text-green-900">{stats.paid}</p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 bg-gray-100 p-2 rounded-lg w-fit">
          {['all', 'en_attente', 'en_retard', 'paye', 'annule'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-md font-medium transition ${
                filterStatus === status
                  ? 'bg-white shadow text-blue-600'
                  : 'text-gray-600 hover:bg-white/50'
              }`}
            >
              {status === 'all' ? 'Tous' :
               status === 'en_attente' ? 'En attente' :
               status === 'en_retard' ? 'En retard' :
               status === 'paye' ? 'Payés' : 'Annulés'}
            </button>
          ))}
        </div>

        {/* Payments Table */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Liste des paiements ({filteredPayments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Dossier</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Montant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Échéance</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Assigné à</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Statut</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPayments.map(payment => (
                      <tr key={payment.id} className={`hover:bg-gray-50 ${isOverdue(payment) ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 text-sm">{payment.client_name}</td>
                        <td className="px-4 py-3 text-sm font-mono text-xs">{payment.file_number}</td>
                        <td className="px-4 py-3 text-sm">{payment.description}</td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600">{payment.amount.toFixed(2)}$</td>
                        <td className="px-4 py-3 text-sm">
                          {format(new Date(payment.due_date), 'dd MMM yyyy', { locale: fr })}
                        </td>
                        <td className="px-4 py-3 text-sm">{payment.employee_name}</td>
                        <td className="px-4 py-3">{getStatusBadge(payment.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {payment.status !== 'paye' && (user?.role === 'admin' || user?.role === 'sub_admin' || payment.assigned_employee_id === user?.id) && (
                              <Button
                                size="sm"
                                onClick={() => handleMarkPaid(payment.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {(user?.role === 'admin' || user?.role === 'sub_admin') && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeletePayment(payment.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Aucun paiement à afficher</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Payment Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Créer un nouveau paiement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Dossier *</Label>
                <Select value={newPayment.file_id} onValueChange={(val) => {
                  setNewPayment({...newPayment, file_id: val});
                  fetchTasksForFile(val); // ✅ AJOUT: Charger les tâches du dossier
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un dossier" />
                  </SelectTrigger>
                  <SelectContent>
                    {files.map(file => (
                      <SelectItem key={file.id} value={file.id}>
                        {file.file_number} - {file.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Client *</Label>
                <Select value={newPayment.client_id} onValueChange={(val) => setNewPayment({...newPayment, client_id: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Employé responsable *</Label>
                <Select value={newPayment.assigned_employee_id} onValueChange={(val) => setNewPayment({...newPayment, assigned_employee_id: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Montant ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Input
                  value={newPayment.description}
                  onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
                  placeholder="Ex: Paiement initial, Acompte, etc."
                />
              </div>

              <div>
                <Label>Date d'échéance *</Label>
                <Input
                  type="date"
                  value={newPayment.due_date}
                  onChange={(e) => setNewPayment({...newPayment, due_date: e.target.value})}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <textarea
                  className="w-full border rounded-md p-2"
                  rows="3"
                  value={newPayment.notes}
                  onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                  placeholder="Notes additionnelles..."
                />
              </div>

              {/* ✅ AJOUT: Section pour cocher les tâches complétées */}
              {tasks.length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <Label className="text-lg font-semibold mb-3 block">Tâches Complétées (pour reçu)</Label>
                  <p className="text-sm text-gray-600 mb-3">
                    Cochez les tâches qui ont été complétées et doivent apparaître sur le reçu
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {tasks.map(task => (
                      <div key={task.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`task-${task.id}`}
                          checked={selectedTasks.includes(task.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTasks([...selectedTasks, task.id]);
                            } else {
                              setSelectedTasks(selectedTasks.filter(id => id !== task.id));
                            }
                          }}
                          className="w-4 h-4 text-green-600 rounded"
                        />
                        <label htmlFor={`task-${task.id}`} className="flex-1 cursor-pointer">
                          <div className="font-medium">{task.title}</div>
                          {task.honoraires && (
                            <div className="text-sm text-gray-600">{task.honoraires} CAD</div>
                          )}
                        </label>
                        {task.status === 'completed' && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            ✓ Complété
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {selectedTasks.length} tâche(s) sélectionnée(s)
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreatePayment} className="bg-green-600 hover:bg-green-700">
                  Créer le paiement
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Payments;
